// Provenance: repo:tds-reconciliation-portal:frontend/src/components/NoRunLoaded.tsx
// (ADR-0005) — ids became UUID strings (a run is shown by its 8-hex tag, the same handle the
// exports print); the clean-slate reasoning carries whole.
import { Play, Upload } from 'lucide-react'
import { Link, useLocation, useNavigate, useParams } from 'react-router'

import { useRuns } from '../api/hooks'
import { runTag } from '../format'
import { withRunParam } from '../workspace/runContext'
import { useWorkspaceSession } from '../workspace/workspaceSession'

/** What a run surface shows when this session has not loaded a run.
 *
 *  Reports, Download and Reconciliation each fell back to `completeRuns[0]` —
 *  the latest complete run — whenever no run was selected. So a fresh session
 *  that had uploaded nothing but a 26AS opened Reports onto a finished run's
 *  documents, and the workspace contradicted its own "nothing is loaded by
 *  default" promise. Falling back to the newest run is the wrong-run hazard the
 *  `?run=` plumbing exists to prevent, applied by default.
 *
 *  Nothing is hidden: every completed run is one click away, and choosing one
 *  is the explicit act that loads it. Choosing keeps you on the surface you
 *  are already looking at rather than routing you somewhere else.
 */
export function NoRunLoaded({ surface }: { surface: string }) {
  const { clientId, periodId } = useParams()
  const pid = periodId ?? ''
  const { open } = useWorkspaceSession()
  const navigate = useNavigate()
  const location = useLocation()
  const runsQ = useRuns(pid)
  const complete = (runsQ.data ?? []).filter((r) => r.status === 'complete')

  const load = (runId: string) => {
    open(pid)
    void navigate(withRunParam(location.pathname + location.search, runId), {
      replace: true,
    })
  }

  if (runsQ.isSuccess && complete.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No completed reconciliation yet — {surface} appear after the first run.
      </p>
    )
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
        Nothing is loaded by default. Open a previous run to see its {surface}, or upload
        fresh data and reconcile — this keeps every session starting from a clean slate.
      </div>
      <div className="flex flex-wrap gap-2">
        <Link
          to={`/w/${clientId}/${pid}/upload`}
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-muted"
        >
          <Upload size={16} /> Upload data
        </Link>
        {complete.length > 0 && (
          <button
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
            onClick={() => load(complete[0].id)}
          >
            <Play size={16} /> Open latest run ({runTag(complete[0].id)})
          </button>
        )}
      </div>
      {complete.length > 1 && (
        <p className="text-sm text-muted-foreground">
          Earlier runs:{' '}
          {complete.slice(1).map((r) => (
            <button
              key={r.id}
              className="mr-1 rounded px-1.5 py-0.5 text-xs text-primary hover:bg-muted"
              onClick={() => load(r.id)}
            >
              {runTag(r.id)}
            </button>
          ))}
        </p>
      )}
    </div>
  )
}
